import { useEffect, useRef, useState } from 'react';
import { playHaptic } from '../haptics';
import { LOGOS } from '../logos';
import { addBalance } from '../balance';
import { playResultSound, stopResultSounds } from '../sounds';
import { playCue } from '../cuelume';
import { ResultActions, ResultSticker } from '../components/GameHeader';

const img = (name) => `${process.env.PUBLIC_URL}/img/${name}`;

const LEVELS = [
  { id: 'easy', label: '3×2', cols: 3, pairs: 3 },
  { id: 'medium', label: '4×4', cols: 4, pairs: 8 },
  { id: 'hard', label: '6×6', cols: 6, pairs: 18 },
];

const TOTAL_PAIRS = LEVELS.reduce((sum, item) => sum + item.pairs, 0);
const TIME_LIMIT = 120;
const MISMATCH_DELAY = 700;
const NEXT_LEVEL_DELAY = 600;

function shuffle(array) {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

// Logos repeat across pairs on big boards; cards match by logo, not by pair id
function buildDeck(pairs) {
  const source = shuffle(LOGOS);
  const cards = [];
  for (let i = 0; i < pairs; i += 1) {
    const logo = source[i % source.length];
    cards.push(
      { key: `${i}-a`, logoId: logo.id, src: logo.src },
      { key: `${i}-b`, logoId: logo.id, src: logo.src }
    );
  }
  // Each card gets its own shine timing so glints never run in sync
  return shuffle(cards).map((card) => ({
    ...card,
    shineDelay: Math.round(Math.random() * 5000),
    shineDuration: Math.round(4000 + Math.random() * 4000),
  }));
}

function formatTime(totalSeconds) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

// Points only for beating all three levels within the time limit
function computeScore(attempts, timeLeft) {
  const base = TOTAL_PAIRS * 150;
  const missPenalty = Math.max(0, attempts - TOTAL_PAIRS) * 20;
  const timeBonus = timeLeft * 2;
  return Math.max(0, base - missPenalty + timeBonus) * 3;
}

function PairGame({ onExit }) {
  const [levelIndex, setLevelIndex] = useState(0);
  const [round, setRound] = useState(0);
  const [deck, setDeck] = useState(() => buildDeck(LEVELS[0].pairs));
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [missed, setMissed] = useState([]);
  const [attempts, setAttempts] = useState(0);
  const [timeLeft, setTimeLeft] = useState(TIME_LIMIT);
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(null); // null | 'won' | 'lost'
  const mismatchTimer = useRef(null);
  const advanceTimer = useRef(null);
  const resultSettled = useRef(false);

  const level = LEVELS[levelIndex];

  useEffect(() => {
    if (!started || finished) return undefined;
    const interval = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          setFinished('lost');
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [started, finished]);

  useEffect(
    () => () => {
      clearTimeout(mismatchTimer.current);
      clearTimeout(advanceTimer.current);
      stopResultSounds();
    },
    []
  );

  useEffect(() => {
    if (!finished || resultSettled.current) return;
    resultSettled.current = true;
    if (finished === 'won') {
      playHaptic('success');
      playResultSound('pairWin');
      // Winning all three levels tops up the site-wide balance
      addBalance(computeScore(attempts, timeLeft));
    }
    if (finished === 'lost') {
      playHaptic('error');
      playResultSound('fail');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished]);

  const openLevel = (index) => {
    setLevelIndex(index);
    setDeck(buildDeck(LEVELS[index].pairs));
    setRound((r) => r + 1);
    setFlipped([]);
    setMatched([]);
    setMissed([]);
    if (index > 0) {
      playHaptic('nudge');
      playCue('arrival');
    }
  };

  const restart = () => {
    clearTimeout(mismatchTimer.current);
    clearTimeout(advanceTimer.current);
    stopResultSounds();
    openLevel(0);
    setAttempts(0);
    setTimeLeft(TIME_LIMIT);
    setStarted(false);
    setFinished(null);
    resultSettled.current = false;
    playHaptic('selection');
  };

  const handleCardClick = (index) => {
    if (finished || matched.includes(index)) return;
    if (flipped.includes(index) || flipped.length === 2) return;

    if (!started) setStarted(true);

    const nextFlipped = [...flipped, index];
    setFlipped(nextFlipped);
    playHaptic('light');
    playCue('tick');

    if (nextFlipped.length < 2) return;

    setAttempts((a) => a + 1);
    const [first, second] = nextFlipped;

    if (deck[first].logoId === deck[second].logoId) {
      const nextMatched = [...matched, first, second];
      setMatched(nextMatched);
      setFlipped([]);

      if (nextMatched.length === deck.length) {
        if (levelIndex === LEVELS.length - 1) {
          setFinished('won');
        } else {
          playHaptic('success');
          playCue('bloom');
          advanceTimer.current = setTimeout(() => {
            openLevel(levelIndex + 1);
          }, NEXT_LEVEL_DELAY);
        }
      } else {
        playHaptic('success');
        playCue('sparkle');
      }
    } else {
      setMissed(nextFlipped);
      playHaptic('error');
      playCue('error');
      mismatchTimer.current = setTimeout(() => {
        setMissed([]);
        setFlipped([]);
      }, MISMATCH_DELAY);
    }
  };

  const timeIsLow = started && !finished && timeLeft <= 30;

  return (
    <div className="app">
      <header className="screen-header">
        <button
          type="button"
          className="icon-btn"
          data-cuelume-press
          data-cuelume-release="droplet"
          onClick={() => {
            stopResultSounds();
            playHaptic('selection');
            onExit();
          }}
          aria-label="Назад"
        >
          <img src={img('icon-arrow-left.svg')} alt="" draggable="false" />
        </button>
        <h1 className="screen-title">Угадай пару</h1>
        <span className="icon-btn static" aria-hidden="true">
          <img src={img('icon-timer.svg')} alt="" draggable="false" />
        </span>
      </header>

      <div className="score-panel" aria-label={`Уровень ${levelIndex + 1} из ${LEVELS.length}`}>
        <div className="score-cell">
          <span className="score-value">
            {finished === 'won' ? LEVELS.length : levelIndex + 1}/{LEVELS.length}
          </span>
          <span className="score-caption">Уровень</span>
        </div>
        <span className="score-divider" aria-hidden="true" />
        <div className="score-cell">
          <span className="score-value">{attempts}</span>
          <span className="score-caption">Попыток</span>
        </div>
        <span className="score-divider" aria-hidden="true" />
        <div className="score-cell">
          <span className={`score-value${timeIsLow ? ' low' : ''}`}>{formatTime(timeLeft)}</span>
          <span className="score-caption">Время</span>
        </div>
      </div>

      <main
        className={`board${level.id === 'hard' ? ' board-hard' : ''}`}
        key={`${level.id}-${round}`}
        style={{ gridTemplateColumns: `repeat(${level.cols}, 1fr)` }}
      >
        {deck.map((card, index) => {
          const isRevealed = flipped.includes(index) || matched.includes(index);
          const isMatched = matched.includes(index);
          const isMissed = missed.includes(index);

          return (
            <button
              key={card.key}
              type="button"
              className={[
                'card',
                isRevealed ? 'is-flipped' : '',
                isMatched ? 'is-matched' : '',
                isMissed ? 'is-missed' : '',
              ].join(' ')}
              style={{
                '--i': index,
                '--shine-delay': `${card.shineDelay}ms`,
                '--shine-duration': `${card.shineDuration}ms`,
              }}
              onClick={() => handleCardClick(index)}
              disabled={isMatched}
              aria-label={isRevealed ? 'Открытая карточка' : 'Закрытая карточка'}
            >
              <span className="card-shake">
                <span className="card-inner">
                  <span className="card-face card-front">
                    <img className="card-mark" src={img('card-back.png')} alt="" draggable="false" />
                  </span>
                  <span className="card-face card-back">
                    <img src={card.src} alt="" draggable="false" />
                  </span>
                </span>
              </span>
            </button>
          );
        })}
      </main>

      {finished && (
        <div className="result" role="dialog" aria-label="Результат игры">
          <div className={`result-card${finished === 'lost' ? ' lost' : ''}`}>
            <ResultSticker won={finished === 'won'} winVideo="sticker-win-pair.mp4" />
            <p className="result-title">
              {finished === 'won' ? 'Все уровни пройдены!' : 'Время вышло'}
            </p>
            <p className="result-score">
              {finished === 'won' ? computeScore(attempts, timeLeft) : 0}
            </p>
            <p className="result-score-label">
              {finished === 'won' ? 'очков' : 'очки не начисляются'}
            </p>
            <div className="result-stats">
              <div className="stat">
                <span className="stat-label">Уровень</span>
                <span className="stat-value">
                  {finished === 'won' ? LEVELS.length : levelIndex + 1}/{LEVELS.length}
                </span>
              </div>
              <div className="stat">
                <span className="stat-label">Попытки</span>
                <span className="stat-value">{attempts}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Время</span>
                <span className="stat-value">{formatTime(TIME_LIMIT - timeLeft)}</span>
              </div>
            </div>
            <ResultActions
              onRestart={restart}
              restartLabel={finished === 'won' ? 'Играть снова' : 'Попробовать снова'}
              onHome={onExit}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default PairGame;
